// The partner's avatar. Generation of the partner themselves lives in
// src/shared/persona.js, which the trial server shares.

// Generate an avatar URL from the DiceBear API based on the partner's traits and
// current mood. Deterministic in name/age so the same person keeps one look.
export const generateAvatarUrl = (character, mood = 'neutral') => {
  const moodMap = {
    happy: 'happy',
    excited: 'happy',
    sad: 'sad',
    neutral: 'neutral',
    bored: 'neutral',
    curious: 'surprised',
    angry: 'angry',
    suspicious: 'concerned',
    condescending: 'smirk',
  };

  const dicebearMood = moodMap[mood] || 'neutral';
  const isMale = character.gender === 'male';

  // 'lorelei' has female-presenting avatars; 'micah' has male-presenting ones.
  // Both styles support the mood/hairColor query params used below.
  const style = isMale ? 'micah' : 'lorelei';

  // Seed from name + age + mood so the look is consistent but expression-aware.
  // The user's description joins the seed, so two people described differently
  // don't share a face. Encoded because a typed name may hold spaces or '&'.
  const seed = encodeURIComponent(
    `${character.name}-${character.age}-${character.description || ''}-${dicebearMood}`
  );

  const hairColorMap = {
    black: '000000',
    brown: '6B4423',
    blonde: 'FFF5E1',
    red: 'A52A2A',
    'dark brown': '3B2F2F',
    'light brown': 'A17A74',
    chestnut: '954535',
    auburn: '922724',
    gray: '9E9E9E',
  };

  const hexColor = hairColorMap[character.physical.hairColor];
  const hairColorParam = hexColor ? `&hairColor=${hexColor}` : '';

  // Deterministic hash of the name so a given character always looks the same.
  const nameSum = character.name.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);

  let customParams;
  if (isMale) {
    customParams = `&facialHairProbability=40&earringsProbability=10`;
  } else {
    const allowedHairVariants = [
      'variant10', 'variant11', 'variant12', 'variant13', 'variant14',
      'variant15', 'variant16', 'variant17', 'variant18', 'variant19',
      'variant20', 'variant21', 'variant24', 'variant29', 'variant30',
      'variant31', 'variant32', 'variant35', 'variant40', 'variant41',
      'variant42', 'variant43', 'variant45', 'variant46', 'variant48',
    ];
    const hairIndex = (nameSum + parseInt(character.age, 10)) % allowedHairVariants.length;
    const selectedHairVariant = allowedHairVariants[hairIndex];
    customParams = `&beardProbability=0&earringsProbability=50&hair=${selectedHairVariant}`;
  }

  return `https://api.dicebear.com/7.x/${style}/svg?seed=${seed}&mood=${dicebearMood}${hairColorParam}${customParams}`;
};
